import * as React from 'react'

import { PlusIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import {
  IPermitTypeField,
  PermitFieldType,
} from '~/client/graph/types.generated'
import ResourceFieldConfigurator from '~/client/src/desktop/components/ResourceFieldConfigurator'
import { Button } from '~/client/src/shared/components/Button/Button'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import { resourcePermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FieldConfigurator from '../../../../FieldConfigurator/FieldConfigurator'
import FormFieldsModal from '../../../../FormFieldsModal'
import { CONDITIONAL_ROW_CONTROL_REVEAL } from '../../rowControlReveal'
import DependentFieldsContainer from '../DependentFieldsConfigurator'
import IDependentFieldsConfiguratorProps from '../types/IDependentFieldsConfiguratorProps'
import { isInnerConfiguratorShown } from '../utils/fieldHelpers'

interface IProps {
  conditionalField: IPermitTypeField
  canAddField: boolean
  restrictedFieldTypes: PermitFieldType[]
  isLBSToggleShown: boolean
  depthLevel: number

  lbsLocationId?: string

  onAddField: (fieldType: PermitFieldType) => void
  onUpdateField: (field: IPermitTypeField) => void
  onRemoveField: (field: IPermitTypeField) => void

  tableConfiguratorRenderer: (field: IPermitTypeField) => JSX.Element

  mainContainerProps: IDependentFieldsConfiguratorProps
}

function ConditionalField({
  conditionalField,
  canAddField,
  restrictedFieldTypes,
  isLBSToggleShown,
  depthLevel,
  lbsLocationId,
  tableConfiguratorRenderer,
  mainContainerProps,
  onAddField,
  onUpdateField,
  onRemoveField,
}: IProps) {
  const showInnerConfigurator = isInnerConfiguratorShown(
    conditionalField,
    depthLevel,
  )

  return (
    <div>
      <div className="row x-center y-center tw:gap-1 tw:group/conditional-row">
        <div className="tw:flex tw:size-7 tw:flex-none! tw:items-center">
          {canAddField && (
            <FormFieldsModal
              addNewField={onAddField}
              restrictedFields={restrictedFieldTypes}
            >
              <Button
                variant="ghost"
                size="icon-sm"
                className={CONDITIONAL_ROW_CONTROL_REVEAL}
                aria-label={Localization.translator.addConditionalField}
              >
                <PlusIcon />
              </Button>
            </FormFieldsModal>
          )}
        </div>

        <FieldConfigurator
          field={conditionalField}
          isLBSToggleShown={isLBSToggleShown}
          onChange={onUpdateField}
          tableConfiguratorRenderer={tableConfiguratorRenderer}
          isMultipleToggleHidden={conditionalField.isUsedInMaterial}
        />

        {!conditionalField.isUsedInMaterial && (
          <Button
            variant="destructive-ghost"
            size="icon-sm"
            aria-label={Localization.translator.delete}
            className={`${CONDITIONAL_ROW_CONTROL_REVEAL} tw:flex-none!`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => onRemoveField(conditionalField)}
          >
            <Trash2Icon />
          </Button>
        )}
      </div>

      {showInnerConfigurator && (
        <DependentFieldsContainer
          field={conditionalField}
          allConditionalFields={mainContainerProps.allConditionalFields}
          depthLevel={mainContainerProps.depthLevel + 1}
          lbsLocationId={lbsLocationId}
          tableConfiguratorRenderer={
            mainContainerProps.tableConfiguratorRenderer
          }
          addNewConditionalField={mainContainerProps.addNewConditionalField}
          updateConditionalField={mainContainerProps.updateConditionalField}
          removeConditionalField={mainContainerProps.removeConditionalField}
          renderNewRuleButton={
            resourcePermitFieldTypes.includes(conditionalField.type)
              ? onClick => (
                  <Button
                    variant="ghost"
                    onClick={onClick}
                    className="tw:text-muted-foreground"
                  >
                    <AddIcon data-icon="inline-start" size="xs" />
                    {Localization.translator.addConditionalField}
                  </Button>
                )
              : undefined
          }
          renderHeader={
            resourcePermitFieldTypes.includes(conditionalField.type)
              ? (startCreating, { rulesCount, isExpanded, onToggle }) => (
                  <ResourceFieldConfigurator.CollapsibleSectionHeader
                    count={rulesCount}
                    isExpanded={isExpanded}
                    onToggle={onToggle}
                    onCreateNew={startCreating}
                    countLabel={n =>
                      Localization.translator.xConditionalFields(n)
                    }
                    sectionLabel={Localization.translator.conditionalFields}
                    addButtonLabel={Localization.translator.addConditionalField}
                  />
                )
              : undefined
          }
        />
      )}
    </div>
  )
}

export default observer(ConditionalField)
